import {
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  inputBinding,
  OnInit,
  resource,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { JwtDecoderService } from '@core/services/jwt-decoder-service';
import { Button } from '@shared/components/button/button';
import { Modal } from '@shared/components/modal/modal';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { User } from '@shared/models/user';
import { UserModal } from './user-modal/user-modal';

@Component({
  selector: 'app-users-page',
  imports: [Button, UserModal, Modal],
  templateUrl: './users-page.html',
})
export class UsersPage implements OnInit {
  private apiClient = inject(ApiClient);
  private router = inject(Router);
  private jwtDecoder = inject(JwtDecoderService);

  // Modal state
  showUserModal = signal<boolean>(false);
  modalMode = signal<'create' | 'edit'>('create');
  userDataForEdit = signal<User | null>(null);

  // Toast functionality
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  // Delete modal
  readonly deleteModal = viewChild.required('deleteModal', { read: Modal });
  private userToDelete = signal<string>('');

  readonly users = resource<User[], void>({
    loader: () => this.apiClient.getAllUsers(),
  });
  readonly isUsersLoading = computed(() => this.users.status() === 'loading');

  readonly usersData = computed(() => this.users.value() as User[] | undefined);

  readonly usersList = signal<User[]>([]);

  ngOnInit() {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const userRole = this.jwtDecoder.getUserRole(token);
        if (userRole != 'admin') {
          this.router.navigate(['/']);
        }
      } catch (error) {
        console.error('Invalid token:', error);
        this.router.navigate(['/']);
      }
    }
  }

  constructor() {
    effect(() => {
      const data = this.users.value(); // User[] or undefined
      if (Array.isArray(data)) {
        const sorted = [...data].sort((a, b) =>
          a.email.toLowerCase().localeCompare(b.email.toLowerCase())
        );
        this.usersList.set(sorted);
      } else {
        this.usersList.set([]);
      }
    });
  }

  // Toast functionality
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  // Modal handlers
  openCreateModal() {
    this.modalMode.set('create');
    this.userDataForEdit.set(null);
    this.showUserModal.set(true);
  }

  openEditModal(userId: string) {
    const user = this.usersList().find(u => u.userId === userId);
    if (user) {
      this.modalMode.set('edit');
      this.userDataForEdit.set(user);
      this.showUserModal.set(true);
    }
  }

  onModalClosed() {
    this.showUserModal.set(false);
    this.userDataForEdit.set(null);
  }

  onUserSaved() {
    this.showUserModal.set(false);
    this.userDataForEdit.set(null);
    this.users.reload();
  }

  readonly searchQuery = signal<string>('');
  readonly filteredUsers = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const keywords = query.split(/\s+/).filter(Boolean); // split by space

    const list = this.usersList();

    return list.filter(user => {
      const fields = [
        user.email.toLowerCase(),
        user.firstName.toLowerCase(),
        user.lastName.toLowerCase(),
        (user.firstName + user.lastName).toLowerCase().replace(/\s+/g, ''),
        user.roles.toLowerCase(),
        user.username.toLowerCase(),
      ];

      // Every keyword must match at least one field
      return keywords.every(keyword =>
        fields.some(field => field.includes(keyword))
      );
    });
  });

  async onDeleteUser(userId: string) {
    this.userToDelete.set(userId);
    this.deleteModal().open();
  }

  async confirmDelete() {
    try {
      const userId = this.userToDelete();
      if (!userId) return;

      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        this.showToast('error', 'Access token is missing. Please login again.');
        return;
      }

      await this.apiClient.deleteUser(userId);
      this.users.reload();
      this.showToast('success', 'User deleted successfully');
      this.userToDelete.set('');
    } catch (error) {
      console.error('Failed to delete user:', error);
      this.showToast('error', 'Failed to delete user. Please try again.');
    }
  }
}
